import { c as _c } from "react/compiler-runtime";
import { useMemo } from 'react';
import type { KeyboardDrawing } from './keyboard-drawing';

/**
 * How a single key is painted: plain, tinted brass (the key under the
 * pointer, or an end of the range), stone (a key still to measure) or the red
 * felt of the key being worked on.
 */
export type PianoKeyTone = 'plain' | 'marked' | 'pending' | 'current';
export interface KeyboardPlateProps {
  /** Drawn over the keys, in the same coordinates. */
  children?: React.ReactNode;
  geometry: KeyboardDrawing;
  /** The key being read right now, painted brass wherever it sits. */
  highlightedKey?: number | null;
  toneByKeyNumber?: ReadonlyMap<number, PianoKeyTone>;
}

/** The keybed shows a sliver of itself below the front of the keys. */
export const KEYBED_OVERHANG = 4;

/** How tall the plate is drawn, keybed included, in the units of its geometry. */
export const keybedHeight = (geometry: KeyboardDrawing): number => geometry.height + KEYBED_OVERHANG;
const joinPaths = (paths: string[]) => paths.join('');

/**
 * The keyboard itself — keybed, felt, keys and, behind the balance rail, the
 * board of the key sticks — as SVG layers without an `<svg>` of their own.
 *
 * Every keyboard of the application is this one drawing: the home page lays
 * it under its figure, `PianoKeyboard` wraps it for the piano and measure
 * pages and for the axis of the charts. A change to how a keyboard looks is
 * therefore made once.
 */
export const KeyboardPlate = t0 => {
  const $ = _c(82);
  const {
    children,
    geometry,
    highlightedKey,
    toneByKeyNumber
  } = t0;
  let t1;
  if ($[0] !== highlightedKey || $[1] !== toneByKeyNumber) {
    const toneOf = keyNumber => keyNumber === highlightedKey ? "marked" : toneByKeyNumber?.get(keyNumber) ?? "plain";
    t1 = (source, tone) => joinPaths(source.filter(key => toneOf(key.number) === tone).map(_temp));
    $[0] = highlightedKey;
    $[1] = toneByKeyNumber;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const pick = t1;
  let t2;
  if ($[3] !== geometry.blackKeys || $[4] !== pick) {
    t2 = pick(geometry.blackKeys, "current");
    $[3] = geometry.blackKeys;
    $[4] = pick;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== geometry.blackKeys || $[7] !== pick) {
    t3 = pick(geometry.blackKeys, "marked");
    $[6] = geometry.blackKeys;
    $[7] = pick;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== geometry.blackKeys || $[10] !== pick) {
    t4 = pick(geometry.blackKeys, "pending");
    $[9] = geometry.blackKeys;
    $[10] = pick;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== geometry.blackKeys) {
    t5 = joinPaths(geometry.blackKeys.map(_temp2));
    $[12] = geometry.blackKeys;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== geometry.keysByNumber || $[15] !== highlightedKey) {
    t6 = highlightedKey === null || highlightedKey === undefined ? "" : geometry.keysByNumber.get(highlightedKey)?.leverPath ?? "";
    $[14] = geometry.keysByNumber;
    $[15] = highlightedKey;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== geometry.whiteKeys || $[18] !== pick) {
    t7 = pick(geometry.whiteKeys, "current");
    $[17] = geometry.whiteKeys;
    $[18] = pick;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== geometry.whiteKeys || $[21] !== pick) {
    t8 = pick(geometry.whiteKeys, "marked");
    $[20] = geometry.whiteKeys;
    $[21] = pick;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== geometry.whiteKeys || $[24] !== pick) {
    t9 = pick(geometry.whiteKeys, "pending");
    $[23] = geometry.whiteKeys;
    $[24] = pick;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== geometry.whiteKeys) {
    t10 = joinPaths(geometry.whiteKeys.map(_temp3));
    $[26] = geometry.whiteKeys;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== t10 || $[29] !== t2 || $[30] !== t3 || $[31] !== t4 || $[32] !== t5 || $[33] !== t6 || $[34] !== t7 || $[35] !== t8 || $[36] !== t9) {
    t11 = {
      blackCurrent: t2,
      blackMarked: t3,
      blackPending: t4,
      blacks: t5,
      readLever: t6,
      whiteCurrent: t7,
      whiteMarked: t8,
      whitePending: t9,
      whites: t10
    };
    $[28] = t10;
    $[29] = t2;
    $[30] = t3;
    $[31] = t4;
    $[32] = t5;
    $[33] = t6;
    $[34] = t7;
    $[35] = t8;
    $[36] = t9;
    $[37] = t11;
  } else {
    t11 = $[37];
  }
  const paths = t11;
  const t12 = geometry.width;
  let t13;
  if ($[38] !== geometry) {
    t13 = keybedHeight(geometry);
    $[38] = geometry;
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  let t14;
  if ($[40] !== geometry.width || $[41] !== t13) {
    t14 = <rect x="0" y="0" width={t12} height={t13} rx="3" className="fill-ptl-keybed" />;
    $[40] = geometry.width;
    $[41] = t13;
    $[42] = t14;
  } else {
    t14 = $[42];
  }
  let t15;
  if ($[43] !== geometry.leverHeight || $[44] !== geometry.width) {
    t15 = geometry.leverHeight > 0 && <rect x="0" y="0" width={geometry.width} height={geometry.leverHeight} className="fill-card" />;
    $[43] = geometry.leverHeight;
    $[44] = geometry.width;
    $[45] = t15;
  } else {
    t15 = $[45];
  }
  let t16;
  if ($[46] !== paths.readLever) {
    t16 = paths.readLever && <path d={paths.readLever} className="fill-ptl-brass-soft" />;
    $[46] = paths.readLever;
    $[47] = t16;
  } else {
    t16 = $[47];
  }
  let t17;
  if ($[48] !== geometry.feltHeight || $[49] !== geometry.leverHeight || $[50] !== geometry.width) {
    t17 = <rect x="0" y={geometry.leverHeight} width={geometry.width} height={geometry.feltHeight} className="fill-ptl-felt" />;
    $[48] = geometry.feltHeight;
    $[49] = geometry.leverHeight;
    $[50] = geometry.width;
    $[51] = t17;
  } else {
    t17 = $[51];
  }
  let t18;
  if ($[52] !== paths.whites) {
    t18 = <path d={paths.whites} strokeWidth="0.8" vectorEffect="non-scaling-stroke" className="fill-ptl-key-white stroke-ptl-key-white-edge" />;
    $[52] = paths.whites;
    $[53] = t18;
  } else {
    t18 = $[53];
  }
  let t19;
  if ($[54] !== paths.whitePending) {
    t19 = paths.whitePending && <path d={paths.whitePending} className="fill-ptl-key-white-pending" />;
    $[54] = paths.whitePending;
    $[55] = t19;
  } else {
    t19 = $[55];
  }
  let t20;
  if ($[56] !== paths.whiteMarked) {
    t20 = paths.whiteMarked && <path d={paths.whiteMarked} className="fill-ptl-key-white-done" />;
    $[56] = paths.whiteMarked;
    $[57] = t20;
  } else {
    t20 = $[57];
  }
  let t21;
  if ($[58] !== paths.whiteCurrent) {
    t21 = paths.whiteCurrent && <path d={paths.whiteCurrent} className="fill-ptl-current" />;
    $[58] = paths.whiteCurrent;
    $[59] = t21;
  } else {
    t21 = $[59];
  }
  let t22;
  if ($[60] !== paths.blacks) {
    t22 = <path d={paths.blacks} className="fill-ptl-key-black" />;
    $[60] = paths.blacks;
    $[61] = t22;
  } else {
    t22 = $[61];
  }
  let t23;
  if ($[62] !== paths.blackPending) {
    t23 = paths.blackPending && <path d={paths.blackPending} className="fill-ptl-key-black-pending" />;
    $[62] = paths.blackPending;
    $[63] = t23;
  } else {
    t23 = $[63];
  }
  let t24;
  if ($[64] !== paths.blackMarked) {
    t24 = paths.blackMarked && <path d={paths.blackMarked} className="fill-ptl-key-black-done" />;
    $[64] = paths.blackMarked;
    $[65] = t24;
  } else {
    t24 = $[65];
  }
  let t25;
  if ($[66] !== paths.blackCurrent) {
    t25 = paths.blackCurrent && <path d={paths.blackCurrent} className="fill-ptl-current" />;
    $[66] = paths.blackCurrent;
    $[67] = t25;
  } else {
    t25 = $[67];
  }
  let t26;
  if ($[68] !== children || $[69] !== t14 || $[70] !== t15 || $[71] !== t16 || $[72] !== t17 || $[73] !== t18 || $[74] !== t19 || $[75] !== t20 || $[76] !== t21 || $[77] !== t22 || $[78] !== t23 || $[79] !== t24 || $[80] !== t25) {
    t26 = <>{t14}{t15}{t16}{t17}{t18}{t19}{t20}{t21}{t22}{t23}{t24}{t25}{children}</>;
    $[68] = children;
    $[69] = t14;
    $[70] = t15;
    $[71] = t16;
    $[72] = t17;
    $[73] = t18;
    $[74] = t19;
    $[75] = t20;
    $[76] = t21;
    $[77] = t22;
    $[78] = t23;
    $[79] = t24;
    $[80] = t25;
    $[81] = t26;
  } else {
    t26 = $[81];
  }
  return t26;
};
function _temp(k) {
  return k.path;
}
function _temp2(key_0) {
  return key_0.path;
}
function _temp3(key_1) {
  return key_1.path;
}